import { ApiError } from '../queries/client';

/**
 * Single classifier for caught errors, shared by the user-facing surface and the
 * telemetry seam so they never drift:
 *
 *   - `userMessage` — a friendly, specific line for the UI (e.g. the create-flow
 *     Callout) instead of a blanket "Something went wrong".
 *   - `summary` — a CONCISE one-line description for RUM (so a `ZodError`'s raw
 *     issues array no longer becomes the js_error_event headline — the full
 *     structured detail still rides in the report payload's `cause`).
 *   - `name` — the classified error name (drives the `com.agentisai.app_error`
 *     metric dimension).
 *
 * Covers the create-flow's real failure modes (RUM-observed): client-side request
 * validation (`ZodError`, the dominant one), server problem-details (`ApiError`),
 * and network failures (Safari "Load failed" / Chrome "Failed to fetch").
 */
export interface DescribedError {
  name: string;
  summary: string;
  userMessage: string;
}

const GENERIC = 'Something went wrong. Please try again.';

/** Minimal shape of a Zod issue we read (duck-typed — no `instanceof` across bundles). */
interface ZodIssueLike {
  code: string;
  path: ReadonlyArray<string | number>;
  maximum?: number;
}
interface ZodErrorLike {
  name: string;
  issues: ZodIssueLike[];
}

function isZodError(e: unknown): e is ZodErrorLike {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as { name?: unknown }).name === 'ZodError' &&
    Array.isArray((e as { issues?: unknown }).issues)
  );
}

/** A `fetch` network-layer failure (NOT an HTTP error status — that's an ApiError). */
function isNetworkError(e: unknown): e is TypeError {
  return (
    e instanceof TypeError &&
    /load failed|failed to fetch|network\s?error|network request failed/i.test(e.message)
  );
}

function userMessageForZod(issues: ZodIssueLike[]): string {
  const tooLong = issues.find((i) => i.code === 'too_big' && typeof i.maximum === 'number');
  if (tooLong) {
    return `Your message is too long (max ${tooLong.maximum!.toLocaleString()} characters). Please shorten it and try again.`;
  }
  if (issues.some((i) => i.code === 'too_small' || i.code === 'invalid_type')) {
    return 'Please enter a message before sending.';
  }
  return 'Please check your input and try again.';
}

function summaryForZod(issues: ZodIssueLike[]): string {
  const first = issues[0];
  const head = first ? `${first.path.join('.') || '(root)'}: ${first.code}` : 'validation failed';
  return `ZodError: ${issues.length} issue${issues.length === 1 ? '' : 's'} — ${head}`;
}

export function describeError(error: unknown): DescribedError {
  // Client-side request validation — the most common create-flow failure.
  if (isZodError(error)) {
    return {
      name: 'ZodError',
      summary: summaryForZod(error.issues),
      userMessage: userMessageForZod(error.issues),
    };
  }

  // Server problem-details (any non-2xx response).
  if (error instanceof ApiError) {
    const detail = error.problem.detail || error.problem.title;
    let userMessage: string;
    if (error.status === 401 || error.status === 403) {
      userMessage = 'Your session may have expired. Please sign in again.';
    } else if (error.status === 429) {
      userMessage = 'Too many requests — please wait a moment and try again.';
    } else if (error.status >= 500) {
      userMessage = GENERIC;
    } else {
      // 4xx (other than auth/429): the BFF's `detail` is a human-written line.
      userMessage = detail || GENERIC;
    }
    return { name: 'ApiError', summary: `ApiError ${error.status}: ${detail}`, userMessage };
  }

  // Network-layer fetch failure.
  if (isNetworkError(error)) {
    return {
      name: 'NetworkError',
      summary: `NetworkError: ${error.message}`,
      userMessage: 'Network error — check your connection and try again.',
    };
  }

  // Fallback.
  const name = error instanceof Error ? error.name || 'Error' : 'UnknownError';
  const summary = error instanceof Error ? error.message : String(error);
  return { name, summary, userMessage: GENERIC };
}
